import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Copy, Check, AlertCircle } from 'lucide-react';

// Maps the current route to the pre-generated llm.txt that mirrors that page.
// Files are emitted by scripts/generate-llm-txt.js into public/ during prestart.
const resolveLlmTxtPath = (pathname) => {
  const trimmed = pathname.replace(/\/+$/, '') || '/';

  if (trimmed === '/') return '/llm.txt';
  if (trimmed === '/about') return '/about/llm.txt';
  if (trimmed === '/blog') return '/llms-full.txt';
  if (trimmed.startsWith('/blog/')) return `${trimmed}/llm.txt`;
  return null;
};

const STATUS_RESET_MS = 2000;

const CopyPageButton = () => {
  const { pathname } = useLocation();
  const [status, setStatus] = useState('idle'); // 'idle' | 'copied' | 'error'
  const timeoutRef = useRef(null);

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  // Reset status when the route changes so a stale "Copied" never lingers.
  useEffect(() => {
    setStatus('idle');
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, [pathname]);

  const txtPath = resolveLlmTxtPath(pathname);

  if (!txtPath) return null;

  const scheduleReset = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setStatus('idle'), STATUS_RESET_MS);
  };

  const handleCopy = async () => {
    try {
      const response = await fetch(txtPath, { cache: 'no-cache' });
      if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
      const text = await response.text();
      await navigator.clipboard.writeText(text);
      setStatus('copied');
      scheduleReset();
    } catch (err) {
      console.error('Copy page failed:', err);
      setStatus('error');
      scheduleReset();
    }
  };

  const tooltipText =
    status === 'copied' ? 'Copied!' : status === 'error' ? 'Failed' : 'Copy page';

  const Icon =
    status === 'copied' ? Check : status === 'error' ? AlertCircle : Copy;

  const iconColorClasses =
    status === 'copied'
      ? 'text-green-600 dark:text-green-400'
      : status === 'error'
        ? 'text-red-600 dark:text-red-400'
        : 'text-gray-600 dark:text-gray-400';

  return (
    <div className="relative group">
      <button
        onClick={handleCopy}
        className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
        aria-label={tooltipText}
      >
        <Icon className={`h-5 w-5 ${iconColorClasses}`} />
      </button>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full mt-1 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 dark:bg-gray-700 px-2 py-1 text-xs font-medium text-white opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
      >
        {tooltipText}
      </span>
    </div>
  );
};

export default CopyPageButton;
